/**
 * InlineInsight — AI insight card that auto-fetches and sits below a chart.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import apiClient from '../../lib/api';

interface InsightData {
  summary: string;
  key_points: string[];
  verdict: string;
}

interface Props {
  industryId: string;
  metric: 'revenue' | 'ebitda' | 'ev' | 'roic';
}

export default function InlineInsight({ industryId, metric }: Props) {
  const [open, setOpen] = useState(true);

  const { data, isLoading, error } = useQuery({
    queryKey: ['industry-insight-v2', industryId, metric],
    queryFn: async () => {
      const res = await apiClient.post<{ metric: string; industry: string; insight: InsightData }>(
        `/analysis/industryInsights?industry_id=${industryId}&metric=${metric}`,
        {},
        { timeout: 120000 }
      );
      return res.data.insight;
    },
    staleTime: 1000 * 60 * 60,
    retry: 1,
  });

  if (error && !data) return null;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50/50 border border-amber-100 rounded-lg">
        <Sparkles className="w-3.5 h-3.5 text-amber-400" />
        <Loader2 className="w-3 h-3 animate-spin text-amber-500" />
        <span className="text-xs text-amber-600">Generating AI insight...</span>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="border border-amber-100 rounded-lg overflow-hidden bg-amber-50/30">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-amber-50/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          <span className="text-xs font-medium text-neutral-700">AI Industry Insight</span>
          {!open && (
            <span className="text-xs text-neutral-500 truncate max-w-[500px]">
              — {data.summary.slice(0, 100)}...
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-neutral-400" /> : <ChevronDown className="w-3.5 h-3.5 text-neutral-400" />}
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-2.5">
          <p className="text-xs text-neutral-600 leading-relaxed">{data.summary}</p>
          <ul className="space-y-1">
            {data.key_points.map((point, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-neutral-500">
                <span className="w-1 h-1 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                {point}
              </li>
            ))}
          </ul>
          <div className="bg-white border border-amber-200 rounded px-3 py-1.5">
            <p className="text-[11px] font-medium text-amber-800">{data.verdict}</p>
          </div>
        </div>
      )}
    </div>
  );
}
